import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Paper,
  Stack,
  CircularProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useDropzone } from 'react-dropzone';
import { getDocxSupportErrorMessage, validateDocxFileSupport } from '../../utils/docxUtils';

interface FileUploadDialogProps {
  /** Called when user confirms upload */
  onUpload: (file: File) => Promise<void>;
  /** Children render prop provides a function to open the dialog */
  children: (openDialog: () => void) => React.ReactNode;
  /** Optional locale switch */
  lang?: 'en' | 'zh-CN' | 'zh-HK';
  /** If true, only accept DOCX files */
  acceptDocxOnly?: boolean;
  isUploading?: boolean;
}

function FileUploadDialog({ onUpload, children, lang = 'en', acceptDocxOnly = false, isUploading = false }: FileUploadDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleClose = () => {
    setIsOpen(false);
    setSelectedFile(null);
    setUploadError(null);
  };

  const handleConfirm = async () => {
    if (!selectedFile) return;

    try {
      setUploadError(null);

      if (selectedFile.name.toLowerCase().endsWith('.docx')) {
        await validateDocxFileSupport(selectedFile);
      }

      await onUpload(selectedFile);
      handleClose();
    } catch (error) {
      setUploadError(getDocxSupportErrorMessage(error, lang));
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      setUploadError(null);
    }
  }, []);

  const handleClearSelectedFile = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setSelectedFile(null);
    setUploadError(null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptDocxOnly
      ? {
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        }
      : {
          'text/plain': ['.txt'],
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
          'text/markdown': ['.md'],
        },
    multiple: false,
    noClick: !!selectedFile,
  });

  return (
    <>
      {children(() => setIsOpen(true))}

      <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{lang === 'en' ? 'Upload Material' : lang === 'zh-HK' ? '上载材料' : '上传材料'}</DialogTitle>
        <DialogContent>
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}
          <Paper
            variant="outlined"
            {...getRootProps()}
            sx={{
              p: 3,
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
          >
            <input {...getInputProps()} />
            <Stack spacing={2} alignItems="center">
              {selectedFile ? (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: 'action.hover',
                  }}
                >
                  <DescriptionOutlinedIcon color="primary" />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {lang === 'en' ? 'Selected file' : lang === 'zh-HK' ? '已選檔案' : '已选文件'}
                    </Typography>
                  </Box>
                  <IconButton aria-label="remove file" edge="end" onClick={handleClearSelectedFile} size="small">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                  <Typography align="center" variant="body1">
                    {isDragActive
                      ? lang === 'en'
                        ? 'Drop the file here...'
                        : lang === 'zh-HK'
                        ? '將檔案拖放到此處...'
                        : '将文件拖放到此处...'
                      : lang === 'en'
                      ? 'Drag and drop a file here, or click to select a file'
                      : lang === 'zh-HK'
                      ? '將檔案拖放到此處，或點擊選擇檔案'
                      : '将文件拖放到此处，或点击选择文件'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" align="center">
                    {acceptDocxOnly
                      ? (lang === 'en' ? 'Supported files: .docx' : lang === 'zh-HK' ? '支援嘅文件：.docx' : '支持的文件：.docx')
                      : (lang === 'en' ? 'Supported files: .txt, .docx, .md' : lang === 'zh-HK' ? '支援嘅文件：.txt, .docx, .md' : '支持的文件：.txt, .docx, .md')
                    }
                  </Typography>
                  <Typography variant="caption" color="text.secondary" align="center">
                    {lang === 'en'
                      ? 'Password-protected Word files are not supported.'
                      : lang === 'zh-HK'
                      ? '不支援受密碼保護的 Word 檔案。'
                      : '不支持受密码保护的 Word 文件。'}
                  </Typography>
                </>
              )}
            </Stack>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isUploading}>{lang === 'en' ? 'Cancel' : lang === 'zh-HK' ? '取消' : '取消'}</Button>
          <Button variant="contained" disabled={!selectedFile || isUploading} onClick={handleConfirm} startIcon={isUploading ? <CircularProgress size={20} /> : undefined}>
            {lang === 'en' ? 'Upload' : lang === 'zh-HK' ? '上载' :  '上传'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default FileUploadDialog;
